<template>
	<div>
		<div v-if="isLoading" class="spinner-container">
			<Spinner />
		</div>
		<div v-if="disbursements.length > 0">
			<hr />

			<h3>Disbursements</h3>

			<div class="select-buttons-container">
				<button
					v-for="disbursement in disbursements"
					:key="disbursement.id"
					:class="['icon-button', { secondary: disbursement != selectedDisbursement }]"
					@click="selectDisbursement(disbursement)"
				>
					<i class="icon icon-money"></i>
					<div class="text-container">
						<span class="main-text">{{ disbursement.disbursedAmountFormatted }}</span>
						<span class="subtext">{{ disbursement.transactionDateDisplay }}</span>
					</div>
				</button>
					<PermissionsWrapper v-if="showAddNewDisbursementButton" :permissionIds="[Permissions.Draws]" :permissionLevel="PermissionLevels.Create">
					<button
						class="icon-button dashed"
						v-if="mode !== 'edit'"
						:disabled="mode == 'add'"
						@click="changeMode('add')"
					>
						<span class="icon">+</span>
						<div class="text-container">
							<span class="main-text">Add New</span>
						</div>
					</button>
				</PermissionsWrapper>
			</div>
			
			<div class="card">
				<PermissionsWrapper :permissionIds="[Permissions.Draws]" :permissionLevel="PermissionLevels.Update">
					<div class="action-button-container">
						<button
							v-if="mode === 'view' && canEditDisbursement"
							:disabled="isSubmitting"
							class="secondary"
							@click="showPostDisbursementChangeModal"
						>
							Post-Disbursement Change
						</button>
						<button v-if="canEditDisbursement && mode !== 'add'" :disabled="mode === 'edit'" @click="changeMode('edit')">
							Edit
						</button>
						<button v-if="mode !== 'view'" :disabled="isSubmitting" @click="handleCancelButtonClick">
							Cancel
						</button>
					</div>
				</PermissionsWrapper>
				<DisbursementForm
					ref="disbursementFormRef"
					:key="selectedDisbursement?.id"
					:mode="mode"
					:fields="formFields"
					:validationSchema="validationSchema"
					:isSubmitting="isSubmitting"
					:initialValues="selectedDisbursement"
					@submit="confirmChanges"
				/>
			</div>
		</div>
		<template v-else-if="disbursements.length === 0 && showAddNewDisbursementButton">
			<hr />
			<h3>Disbursements</h3>
			<div class="select-buttons-container">
				<PermissionsWrapper v-if="showAddNewDisbursementButton" :permissionIds="[Permissions.Draws]" :permissionLevel="PermissionLevels.Create">
					<button
						class="icon-button dashed"
						v-if="mode !== 'edit'"
						:disabled="mode == 'add'"
						@click="changeMode('add')"
					>
						<span class="icon">+</span>
						<div class="text-container">
							<span class="main-text">Add New</span>
						</div>
					</button>
				</PermissionsWrapper>
				</div>
				<div class="card" v-if="mode === 'add'">
					<PermissionsWrapper :permissionIds="[Permissions.Draws]" :permissionLevel="PermissionLevels.Update">	
						<div class="action-button-container">
							<button :disabled="isSubmitting" @click="handleCancelButtonClick">
								Cancel
							</button>
						</div>
					</PermissionsWrapper>
					<DisbursementForm
						ref="disbursementFormRef"
						:key="selectedDisbursement?.id"
						:mode="mode"
						:fields="formFields"
						:validationSchema="validationSchema"
						:isSubmitting="isSubmitting"
						:initialValues="selectedDisbursement"
						@submit="confirmChanges"
					/>
				</div>
		</template>

		<PostDisbursementChangeForm
			v-if="postDisbursementChangeFormVisible"
			:isSubmitting="isSubmitting"
			:fields="changeFormFields"
			:validationSchema="changeValidationSchema"
			@submit="createPostDisbursementChangeAsync"
			@close="closePostDisbursementChangeModal"
		/>
	</div>
</template>

<script setup lang="ts">
	import '@/assets/css/card.css';
	import { defineProps, onMounted, ref, watch, computed } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import { Disbursement, Draw, ValidDisbursementStatusIds, FormField, DrawStatuses } from '@/models/loans';
	import PostDisbursementChangeForm from './PostDisbursementChangeForm.vue';
	import DisbursementForm from './DisbursementForm.vue';
	import { useDisbursementFormFields } from '@/composables/loan/useDisbursementForm';
	import { usePostDisbursementChangeFormFields } from '@/composables/loan/usePostDisbursementChangeForm';
	import { useDisbursementData } from '@/composables/loan/useDisbursementData';
	import { usePostDisbursementChangeData } from '@/composables/loan/usePostDisbursementChangeData';
	import { RootStore } from "@/types/root-store";
	import store from "@/store";
	import { FormChangesResponse, FormChange } from "@/models/form";
	import { DisbursementStatusIds } from "@/types/disbursements";

	const props = defineProps<{
		loanId: number;
		draw: Draw;
		onDisbursementChangedAsync: () => Promise<void>;
	}>();

	const route = useRoute();
	const router = useRouter();

	const disbursementFormRef = ref<InstanceType<typeof DisbursementForm> | null>(null);

	const isLoading = ref<boolean>(true);
	const isSubmitting = ref<boolean>(false);
	const disbursements = ref<Disbursement[]>([]);
	const selectedDisbursement = ref<Disbursement | null>(null);
	const previousDisbursement = ref<Disbursement | null>(null);
	const mode = ref<'view' | 'edit' | 'add'>('view');
	const postDisbursementChangeFormVisible = ref<boolean>(false);
	
	const { disbursementStatusOptions, disbursementStatusReasonOptions } = useDisbursementData();
	const { formFields, validationSchema, keysToSkip, keysToRename } = useDisbursementFormFields({
		selectedDisbursement,
		mode,
		disbursementStatusOptions,
		disbursementStatusReasonOptions,
		loanId: computed(() => props.loanId),
		drawId: computed(() => props.draw.id),
		disbursements: disbursements,
		draw: computed(() => props.draw),
	});

	const { disbursementCancellationReasonOptions } = usePostDisbursementChangeData();
	const { changeFormFields, changeValidationSchema } = usePostDisbursementChangeFormFields({
		selectedDisbursement,
		disbursementCancellationReasonOptions
	});

	const oidcUser = computed(() => (store.state as RootStore)?.oidcStore?.user);

	const hasDrawRequestedAmount = computed<boolean>(() => {
		const value = props?.draw?.requestedAmount; 
		return (value !== null && value !== undefined && value > 0);
	});

	const showAddNewDisbursementButton = computed<boolean>(() => {
		const validDrawStatuses = [ DrawStatuses.Certified, DrawStatuses.LenderHold ];
		return disbursements.value?.length < 4 && validDrawStatuses.includes(props.draw.drawStatusTypeId) && hasDrawRequestedAmount.value;
	});

	const changeMode = (newMode: 'view' | 'edit' | 'add') => {
		if (newMode === 'add') {
			
			const disbCount = disbursements.value.length;
			const sequence = `${ disbCount.toString().length < 2 ? '0' :''}${disbCount + 1}`;

			previousDisbursement.value = selectedDisbursement.value;
			selectedDisbursement.value = new Disbursement({
				sequenceNumber: sequence,
				disbursementStatusTypeId: DisbursementStatusIds.SCHEDULED_DISBURSEMENT,
				certifiedAmount: props.draw.certifiedAmount
			});		
		}
		else if (newMode ==='view' && mode.value === 'add') {
			if (previousDisbursement.value) {
				selectedDisbursement.value = previousDisbursement.value;
			} else {
				selectedDisbursement.value = null;
			}
		}
		else if (newMode === 'view') {
			if (previousDisbursement.value) {
				selectedDisbursement.value = previousDisbursement.value;
			}
		}

		mode.value = newMode;
	};

	const canEditDisbursement = computed(() => {
		return ValidDisbursementStatusIds.includes(selectedDisbursement.value?.disbursementStatusTypeId || 0);
	});

	const selectDisbursement = (disbursement: Disbursement) => {
		selectedDisbursement.value = disbursement;

		mode.value = 'view';

		router.replace({
			query: { ...route.query, disbursementId: disbursement.id },
			hash: route.hash
		});
	};

	const showPostDisbursementChangeModal = () => (postDisbursementChangeFormVisible.value = true);

	const closePostDisbursementChangeModal = () => (postDisbursementChangeFormVisible.value = false);

	function getFormChanges() {
		const prefix = `Draw Seq. ${props.draw.sequenceNumber} / Disbursement Seq. ${disbursementFormRef.value?.formData?.sequenceNumber}`
		
		const options = {
			keysToSkip: keysToSkip,
			keysToRename: keysToRename,
			commentPrefix: mode.value === 'edit' || mode.value === 'add' ? `${prefix} ${mode.value === 'add' ? 'Added' : 'Updated'} | ` : null,
			userDisplayName: `${oidcUser.value.given_name} ${oidcUser.value.family_name}`,
			mode: mode.value
		};

		const initialValues = disbursementFormRef.value?.initialValuesProp;
		const updatedValues =  disbursementFormRef.value?.formData;

		if (updatedValues && updatedValues.comment) {
			updatedValues.comment = updatedValues.comment.trim();
		}

		const formChanges = functions.summarizeFormChanges(updatedValues, initialValues, options);
		const modalHtml = functions.buildFormChangesTable(formChanges.changes, mode.value);

		return { modalHtml, formChanges };
	}

	function discardChanges() {
		changeMode('view');
	}

	async function handleCancelButtonClick() {
		const { modalHtml, formChanges } = getFormChanges();
		const validateFormResponse =  await disbursementFormRef.value?.validate();
		const btnTitle = validateFormResponse?.valid ? 'Save and Close' : 'Resume Editing';
		// Check if changes exist
		if (modalHtml) {
			functions.openModal({
				title: "Alert",
				description: "The changes you made have not been saved.",
				buttons: [
					{ title: "Cancel", onClick: () => 
						{
							// exits the Edit mode without saving any changes
							discardChanges();
							functions.closeModal();
						} 
					},
					{ title: btnTitle, onClick: async () => {
							if (validateFormResponse?.valid) {
								// Presents the confirm your changes modal 
								if (disbursementFormRef.value) {
									await confirmChanges(disbursementFormRef.value?.formData)
								}
							} else {
								functions.closeModal();
							}
						} 
					},
				],
			});
		} else {
			discardChanges();
		}
	}

	async function createPostDisbursementChangeAsync(formData: Record<string, any>) {
		const disbursement = selectedDisbursement.value;

		if (!disbursement) {
			return;
		}

		isSubmitting.value = true;

		try {
			await axios.post(`/api/loanservice/disbursements/${disbursement.id}/post-disbursement-change`, {
				disbursementId: disbursement.id,
				originalDisbursementDate: disbursement.transactionDate,
				originalDisbursementAmount: disbursement.disbursedAmount,
				...formData
			});

			closePostDisbursementChangeModal();

			await props.onDisbursementChangedAsync();
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue creating a post-disbursement change. Please try again later.'
			});
		} finally {
			isSubmitting.value = false;
		}
	}

	async function confirmChanges(formData: Record<string, any>) {
		const { modalHtml, formChanges } = getFormChanges();

		if (modalHtml) {
			functions.openModal({
				title: "Confirm",
				description: "Please review your changes before submitting...",
				html: modalHtml,
				buttons: [
					{ title: "Cancel", onClick: () => functions.closeModal() },
					{ title: "Save Changes", onClick: async () => {
							functions.setModalLoading(true);
							await saveChangesAsync(formData, formChanges) 
						} 
					},
				],
			});
		}
	}
	
	async function saveChangesAsync(formData: Record<string, any>, formChanges: FormChangesResponse) {
		if (mode.value === 'add') {
			await createDisbursementAsync(formData, formChanges);

			if (disbursements.value.length > 0) {
				selectDisbursement(disbursements.value[disbursements.value.length - 1]); // select the newest disbursement
			}
		} else if (mode.value === 'edit') {			
			await updateDisbursementAsync(formData, formChanges);
		}
	}

	async function createDisbursementAsync(formData: Record<string, any>, formChanges: FormChangesResponse) {
		isSubmitting.value = true;

		try {
			const request = {
				createDisbursementRequest: {
					drawId: props.draw.id,
					...formData
				},
				certificationMethodTypeId: props.draw.certificationMethodId,
				// Under v2 `draw.id` holds a certification id, so send it explicitly rather than
				// leaving the backend to guess which id space `drawId` is in.
				certificationId: props.draw.certificationId,
			}

			await axios.post(`/api/loanservice/loan/${props.loanId}/draw/${props.draw.id}/disbursement`, request);

			if (formChanges.comment) {
				await createCommentAsync(formChanges.comment);
			}

			await props.onDisbursementChangedAsync();
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue creating a disbursement. Please try again later.'
			});
		} finally {
			isSubmitting.value = false;
		}
	}

	async function updateDisbursementAsync(formData: Record<string, any>, formChanges: FormChangesResponse) {
		const disbursement = selectedDisbursement.value;

		if (!disbursement) {
			return;
		}

		isSubmitting.value = true;

		try {
			const request = {
				id: disbursement.id,
				disbursementTypeId: disbursement.disbursementTypeId,
				...formData
			};

			await axios.post(`/api/loanservice/disbursements/${disbursement.id}`, request);

			if (formChanges.comment) {
				await createCommentAsync(formChanges.comment);
			}

			await props.onDisbursementChangedAsync();
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue updating the disbursement. Please try again later.'
			});
		} finally {
			isSubmitting.value = false;
		}
	}

	async function createCommentAsync(comment: string) {
		const request = {
			loanId: props.loanId,
			comment
		};

		await axios.post('/api/comments/loan', request);
	}

	async function getDisbursementsAsync() {
		try {
			isLoading.value = true;

			const response = await axios(`/api/loanservice/draws/${props.draw.id}/disbursements`);

			disbursements.value = response.data;

			selectInitialDisbursement();
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue getting the disbursements. Please try again later.'
			});
		} finally {
			isLoading.value = false;
		}
	}

	const selectInitialDisbursement = () => {
		const queryDisbursementId = route.query.disbursementId;
		const firstDisbursement = disbursements.value[0];

		if (disbursements.value.length === 0) {
			router.replace({
				query: { ...route.query, disbursementId: undefined },
				hash: route.hash
			});

			return;
		}

		const matchingDisbursement = disbursements.value.find(
			(disbursement) => disbursement.id === Number(queryDisbursementId)
		);

		selectDisbursement(matchingDisbursement || firstDisbursement);
	};

	watch(
		() => props.draw,
		async () => {
			await getDisbursementsAsync(); // todo: fix on adding new draw...
		}
	);

	onMounted(async () => {
		await getDisbursementsAsync();
	});
</script>

<style scoped src="@/assets/css/loans/draw-disbursement-details.css"></style>
